import Card from "components/card";
import React, { useCallback, useEffect, useState } from "react";
import FeedbackDataTable from "./components/FeedbackDataTable";
import Loader from "components/Loader";
import {
  fetchFeedbackStatuses,
  fetchFeedbackTypes,
  searchFeedbacks,
} from "api/axios";
import {
  Feedback,
  FeedbackStatusEnum,
  FeedbackTypeEnum,
  FeedbackTypeOptions,
} from "types";
import {
  getStatusTranslationKey,
  getTypeTranslationKey,
  handleAxiosAndCaughtError,
} from "utils";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Feedbacks = () => {
  const { t } = useTranslation();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackTypes, setFeedbackTypes] = useState<FeedbackTypeOptions[]>([]);
  const [feedbackStatuses, setFeedbackStatuses] = useState<
    FeedbackTypeOptions[]
  >([]);
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();

  // Get the feedback ID from URL params (e.g., /feedback/123) or query params (e.g., /feedback?id=123)
  const feedbackIdFromUrl = id || searchParams.get("id");

  // Fetch feedback types on component mount
  const fetchFeedbackTypesData = useCallback(async () => {
    try {
      const response = await fetchFeedbackTypes();
      const typesArray = Array.isArray(response)
        ? response
        : response.feedback_types || [];

      const translatedTypes = typesArray.map(
        (item: { value: string; label: string }) => ({
          value: item.value,
          label: t(getTypeTranslationKey(item.value as FeedbackTypeEnum)),
        })
      );
      setFeedbackTypes(translatedTypes);
    } catch (err) {
      console.error("Failed to fetch feedback types:", err);
      // Fallback to hardcoded types if API fails
      setFeedbackTypes([
        { value: FeedbackTypeEnum.CONTRIBUTE, label: t("feedback.contribute") },
        { value: FeedbackTypeEnum.DISLIKE, label: t("feedback.dislike") },
      ]);
    }
  }, [t]);

  // Fetch feedback statuses on component mount
  const fetchFeedbackStatusesData = useCallback(async () => {
    try {
      const response = await fetchFeedbackStatuses();
      const statusesArray = Array.isArray(response)
        ? response
        : response.feedback_statuses || [];

      const translatedStatuses = statusesArray.map(
        (item: { value: string; label: string }) => ({
          value: item.value,
          label: t(getStatusTranslationKey(item.value as FeedbackStatusEnum)),
        })
      );
      setFeedbackStatuses(translatedStatuses);
    } catch (err) {
      console.error("Failed to fetch feedback statuses:", err);
      // Fallback to hardcoded statuses if API fails
      setFeedbackStatuses([
        { value: FeedbackStatusEnum.PENDING, label: t("feedback.pending") },
        { value: FeedbackStatusEnum.APPROVED, label: t("feedback.approved") },
        { value: FeedbackStatusEnum.REJECTED, label: t("feedback.rejected") },
      ]);
    }
  }, [t]);

  const fetchFeedbacks = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        // Extract search parameters from URL
        const feedbackType = searchParams.get(
          "feedback_type"
        ) as FeedbackTypeEnum | null;
        const feedbackStatus = searchParams.get(
          "feedback_status"
        ) as FeedbackStatusEnum | null;
        const handler = searchParams.get("handler");
        const workspaceId = searchParams.get("workspace_id");

        const resp = await searchFeedbacks({
          feedback_type: feedbackType || undefined,
          feedback_status: feedbackStatus || undefined,
          handler: handler || undefined,
          workspace_id: workspaceId || undefined,
          take: 999,
          skip: 0,
        });

        setFeedbacks(resp.items || []);
      } catch (err: any) {
        if (signal?.aborted || err.name === "CanceledError") {
          return;
        }
        setError(t("feedback.clickToRetry"));
        setFeedbacks([]);
        handleAxiosAndCaughtError(err);
      } finally {
        setLoading(false);
      }
    },
    [searchParams, t]
  );

  useEffect(() => {
    const abortController = new AbortController();

    // Fetch feedback types, statuses, and feedback
    const fetchData = async () => {
      await Promise.all([
        fetchFeedbackTypesData(),
        fetchFeedbackStatusesData(),
        fetchFeedbacks(abortController.signal),
      ]);
    };

    fetchData();

    return () => abortController.abort();
  }, [fetchFeedbacks, fetchFeedbackTypesData, fetchFeedbackStatusesData]);

  const filterOptions = {
    feedback_type: feedbackTypes,
    feedback_status: feedbackStatuses,
  };

  return (
    <Card extra={" relative w-full  h-[80vh] md:mt-3 mt-12 px-6 "}>
      {loading ? (
        <Loader
          txt={t("feedback.loadingFeedback")}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      ) : error ? (
        <div
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2
        -translate-y-1/2 flex-col items-center justify-center py-10
         "
        >
          <span className="mb-4 text-red-500">{error}</span>
          <button
            className="rounded bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600"
            onClick={() => fetchFeedbacks()}
          >
            {t("common.retry")}
          </button>
        </div>
      ) : (
        <FeedbackDataTable
          fetchFeedbacks={fetchFeedbacks}
          tableData={feedbacks || []}
          filterOptions={filterOptions}
          initialFeedbackId={feedbackIdFromUrl}
        />
      )}
    </Card>
  );
};

export default Feedbacks;
